import type { Timestamp } from "firebase/firestore";
import bigLogo from "@/assets/big-logo.png";

interface MaintenancePageProps {
  message: string | null;
  estimatedEnd: Timestamp | null;
  contactEmail: string;
  scope: "platform" | "org";
  orgName: string | null;
}

function formatEta(ts: Timestamp): string {
  const date = ts.toDate();
  return date.toLocaleString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function MaintenancePage({
  message,
  estimatedEnd,
  contactEmail,
  scope,
  orgName,
}: MaintenancePageProps) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-slate-900 px-4">
      <div className="w-full max-w-lg text-center">
        {/* Logo */}
        <img
          src={bigLogo}
          alt="Big Impact Group"
          className="mx-auto mb-8 h-12 brightness-0 invert"
        />

        {/* Badge */}
        <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-teal/30 bg-teal/10 px-4 py-1.5">
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal opacity-75" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-teal" />
          </span>
          <span className="font-heading text-sm font-semibold tracking-wide text-teal">
            Scheduled Maintenance
          </span>
        </div>

        {/* Scope indicator for org-level */}
        {scope === "org" && orgName && (
          <p className="mb-4 font-body text-sm text-slate-400">{orgName}</p>
        )}

        {/* Headline */}
        <h1 className="mb-4 font-heading text-3xl font-bold text-white">
          {message || "We're making things better."}
        </h1>

        {/* Body */}
        <p className="mb-8 font-body text-base leading-relaxed text-slate-300">
          We're performing scheduled upgrades to improve your experience. Your
          data is safe and all services will be restored shortly.
        </p>

        {/* ETA card */}
        {estimatedEnd && (
          <div className="mx-auto mb-8 max-w-xs rounded-lg border border-slate-700 bg-slate-800 px-6 py-4">
            <p className="mb-1 font-body text-xs uppercase tracking-wider text-slate-400">
              Estimated completion
            </p>
            <p className="font-heading text-lg font-semibold text-white">
              {formatEta(estimatedEnd)}
            </p>
          </div>
        )}

        {/* Contact */}
        <p className="mb-12 font-body text-sm text-slate-400">
          Need something urgent? Reach us at{" "}
          <a
            href={`mailto:${contactEmail}`}
            className="text-teal underline decoration-teal/40 hover:decoration-teal"
          >
            {contactEmail}
          </a>
        </p>

        {/* Footer */}
        <p className="font-body text-xs text-slate-500">
          Powered by BiG &mdash; bigimpactgroup.ai
        </p>
      </div>
    </div>
  );
}
